/**
 * Re-check the market status of pipeline deals against the property API.
 *
 * Thin client for the `listing-status-refresh` edge function, which does the API
 * calls and writes `pipeline_properties.listing_status` +
 * `listing_status_checked_at`. Nothing here talks to the API directly — the key
 * is server-side only.
 *
 * Pass no ids to sweep the least-recently-checked not-yet-owned deals (the same
 * mode a cron schedule would use); pass ids to re-check specific deals.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { toUserMessage } from "@/lib/supabase-errors";
import type { ListingStatus } from "@/lib/listing-status";

export interface RefreshListingStatusArgs {
  /** Specific deals to re-check. Omit for the stale-first sweep. */
  pipelinePropertyIds?: string[];
  /** Max rows per run when sweeping. The function caps this at 100. */
  limit?: number;
  /**
   * Suppress the summary/error toasts. For checks that happen inside another
   * interaction — the Closed-Won drop gate — where the caller owns the
   * messaging and a "Checked 1." toast would just be noise. Cache invalidation
   * still happens either way.
   */
  silent?: boolean;
}

interface RowOutcome {
  id: string;
  address: string;
  from: ListingStatus;
  to?: ListingStatus;
  changed: boolean;
  /** The API had no status we could read — the row was left untouched. */
  unresolved?: boolean;
  raw?: string;
  /** Which MLSSearch lookup worked: `id:<field>`, "zip", or "none". */
  via: string;
}

export interface RefreshListingStatusResult {
  checked: number;
  changed: number;
  unresolved: number;
  /** Addresses that came back sold — these can no longer be acquired. */
  nowSold: string[];
  /**
   * Distinct lookup strategies that resolved a status this run. MLSSearch has no
   * documented listing-id filter, so the function probes candidate field names
   * and reports the winner here — check it after the first real run.
   */
  resolvedVia: string[];
  outcomes: RowOutcome[];
  checkedAt: string;
}

/**
 * Summarize a run for the toast. Sold properties are called out by name: that's
 * the outcome the user actually needs to act on.
 */
export function summarizeRefresh(result: RefreshListingStatusResult): string {
  if (result.checked === 0 && result.unresolved === 0) {
    return "No pipeline deals needed a market check.";
  }

  const parts = [`Checked ${result.checked}`];
  if (result.changed > 0) parts.push(`${result.changed} changed`);
  if (result.unresolved > 0) parts.push(`${result.unresolved} unavailable`);

  const sold =
    result.nowSold.length > 0
      ? ` Now sold: ${result.nowSold.slice(0, 3).join(", ")}${
          result.nowSold.length > 3 ? ` +${result.nowSold.length - 3} more` : ""
        }.`
      : "";

  return `${parts.join(" · ")}.${sold}`;
}

export interface CheckListingStatusArgs {
  /** MLS listing id — `MockProperty.id`. */
  propertyId?: string | null;
  /** Property snapshot; the function reads address/city/state/zip off it. */
  property?: Record<string, unknown> | null;
}

export interface CheckListingStatusResult {
  status: ListingStatus | null;
  /** True only for a confirmed sale. An unresolved lookup is never `sold`. */
  sold: boolean;
  unresolved: boolean;
  address: string;
  raw?: string;
  via: string;
  checkedAt: string;
}

/**
 * Resolve a property's market status *before* it has a `pipeline_properties`
 * row, so a caller can refuse to create one for a listing that already sold.
 *
 * {@link useRefreshListingStatus} can't do this: it works from stored rows, so
 * by the time it has an id to check, the write has already happened.
 *
 * Reads nothing and writes nothing — pure lookup.
 */
export function useCheckListingStatus() {
  return useMutation({
    mutationFn: async (
      args: CheckListingStatusArgs,
    ): Promise<CheckListingStatusResult> => {
      const { data, error } =
        await supabase.functions.invoke<CheckListingStatusResult>(
          "listing-status-refresh",
          { body: { probe: args } },
        );
      if (error) throw error;
      if (!data) throw new Error("Listing status check returned no result");
      return data;
    },
  });
}

export function useRefreshListingStatus() {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args: RefreshListingStatusArgs = {},
    ): Promise<RefreshListingStatusResult> => {
      const { data, error } =
        await supabase.functions.invoke<RefreshListingStatusResult>(
          "listing-status-refresh",
          { body: args },
        );
      if (error) throw error;
      if (!data) throw new Error("Market status refresh returned no result");
      return data;
    },
    onSuccess: (result, args) => {
      // The board, the analytics row and the Properties page all read the rows
      // this just rewrote.
      void queryClient.invalidateQueries({
        queryKey: ["pipeline_properties", currentWorkspace?.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["pipeline_properties_owned", currentWorkspace?.id],
      });

      if (args?.silent) return;

      // A sold listing is bad news, not a success — say so with a warning.
      if (result.nowSold.length > 0) {
        toast.warning(summarizeRefresh(result));
      } else {
        toast.success(summarizeRefresh(result));
      }
    },
    onError: (err, args) => {
      if (args?.silent) {
        // The caller decides what a failed check means. For the Closed-Won gate
        // that's "fall back to the stored status", not "block the user".
        console.error("Silent market status refresh failed:", err);
        return;
      }
      toast.error(toUserMessage(err, "Failed to refresh market status"));
    },
  });
}
